class SiteAccordion extends HTMLElement {
  static DESKTOP_QUERY = "(min-width: 1024px)";
  static DURATION = 300;

  connectedCallback() {
    this.isDesktop = false;
    this.handleClick = this.onClick.bind(this);
    this.addEventListener("click", this.handleClick);

    if (this.hasAttribute("data-desktop-open")) {
      this.mediaQuery = window.matchMedia(SiteAccordion.DESKTOP_QUERY);
      this.handleBreakpoint = this.onBreakpoint.bind(this);
      this.mediaQuery.addEventListener("change", this.handleBreakpoint);
      this.onBreakpoint(this.mediaQuery);
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleBreakpoint);
    }
  }

  onClick(event) {
    const summary = event.target.closest("summary");
    if (!summary) return;

    const details = summary.closest("details");
    if (!details || !this.contains(details)) return;

    // Prevent native toggle — we control it
    event.preventDefault();

    if (details.open) {
      this.collapse(details);
    } else {
      // Panels open independently by design — opening one never closes a sibling.
      // The old one-at-a-time coupling closed a sibling mid-animation, which
      // caused the MIT-52 end-of-close snap and the MIT-35 open skip. Do not
      // reintroduce it.
      this.expand(details);
    }
  }

  expand(details) {
    // Cancel any running animation
    if (details._accordionAnim) {
      details._accordionAnim.cancel();
      details._accordionAnim = null;
    }

    const body = details.querySelector(
      ".accordion__body, .footer-menu-block__body",
    );
    if (!body) {
      details.open = true;
      return;
    }

    // Set open so content is measurable, start from 0fr
    details.open = true;
    body.style.gridTemplateRows = "0fr";

    // Force layout, then animate to 1fr
    requestAnimationFrame(() => {
      const anim = body.animate(
        [{ gridTemplateRows: "0fr" }, { gridTemplateRows: "1fr" }],
        { duration: SiteAccordion.DURATION, easing: "ease" },
      );

      anim.onfinish = () => {
        body.style.gridTemplateRows = "";
        details._accordionAnim = null;
      };

      details._accordionAnim = anim;
    });
  }

  collapse(details) {
    if (details._accordionAnim) {
      details._accordionAnim.cancel();
      details._accordionAnim = null;
    }

    const body = details.querySelector(
      ".accordion__body, .footer-menu-block__body",
    );
    if (!body) {
      details.open = false;
      return;
    }

    const anim = body.animate(
      [{ gridTemplateRows: "1fr" }, { gridTemplateRows: "0fr" }],
      { duration: SiteAccordion.DURATION, easing: "ease" },
    );

    anim.onfinish = () => {
      details.open = false;
      body.style.gridTemplateRows = "";
      details._accordionAnim = null;
    };

    details._accordionAnim = anim;
  }

  onBreakpoint(queryOrEvent) {
    this.isDesktop = queryOrEvent.matches;
    const details = this.querySelectorAll("details");

    for (const detail of details) {
      // Cancel any running animation
      if (detail._accordionAnim) {
        detail._accordionAnim.cancel();
        detail._accordionAnim = null;
      }

      if (this.isDesktop) {
        detail.setAttribute("open", "");
      } else {
        detail.removeAttribute("open");
      }
    }
  }
}

if (!customElements.get("site-accordion")) {
  customElements.define("site-accordion", SiteAccordion);
}
